import { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

import type { UploadPreview } from '@smartapply/shared';

import { SmartApplyApiError, uploadPdf } from '../../lib/api-client.js';
import { cn } from '../../lib/cn.js';

interface UploadZoneProps {
  onUploaded: (preview: UploadPreview) => void;
}

type UploadState = 'idle' | 'reading' | 'parsing' | 'done' | 'error';

export function UploadZone({ onUploaded }: UploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<UploadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  function pickFile() {
    fileInputRef.current?.click();
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0]!;
    setFileName(file.name);
    setError(null);
    setState('reading');

    // Pequeno delay artificial para dar fluidez à signature (não falso —
    // a leitura local e o request acontecem mesmo, só sincronizamos com a animação)
    await new Promise((r) => setTimeout(r, 300));
    setState('parsing');

    try {
      const preview = await uploadPdf(file);
      setState('done');
      onUploaded(preview);
    } catch (err) {
      setState('error');
      if (err instanceof SmartApplyApiError) {
        setError(err.message);
      } else {
        setError('Falha ao subir o PDF.');
      }
    }
  }

  return (
    <section>
      <div className="mb-6">
        <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          arquivo de origem
        </p>
        <h2 className="font-serif text-2xl font-medium text-[var(--ink-primary)]">
          PDF do seu perfil
        </h2>
        <p className="mt-2 font-sans text-sm text-[var(--ink-secondary)]">
          No LinkedIn: <span className="font-medium">Recursos &rsaquo; Salvar em PDF</span>. O
          arquivo é processado em memória e descartado quando a sessão expirar.
        </p>
      </div>

      <div
        className={cn(
          'card-paper relative overflow-hidden rounded-sm px-7 py-9 transition-colors',
          state === 'idle' && 'border-l-[3px] border-[var(--border-default)]',
          state === 'reading' && 'border-l-[3px] border-[var(--accent-saffron)]',
          state === 'parsing' && 'border-l-[3px] border-[var(--accent-saffron)]',
          state === 'done' && 'border-l-[3px] border-[var(--accent-forest)]',
          state === 'error' && 'border-l-[3px] border-[var(--accent-crimson)]',
        )}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          void handleFiles(e.dataTransfer.files);
        }}
      >
        {/* Decoração: linhas pautadas suaves no fundo da ficha */}
        <div
          aria-hidden
          className="ruled-bg absolute inset-0 -z-0 opacity-30"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => void handleFiles(e.target.files)}
        />

        <div className="relative z-10">
        <AnimatePresence mode="wait">
          {state === 'idle' && (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-start gap-5"
            >
              <PaperIcon />
              <div>
                <p className="font-serif text-lg font-medium text-[var(--ink-primary)]">
                  Suba o PDF do seu perfil
                </p>
                <p className="mt-1 font-sans text-sm text-[var(--ink-tertiary)]">
                  Arraste o arquivo aqui ou{' '}
                  <button
                    type="button"
                    onClick={pickFile}
                    className="font-medium text-[var(--accent-saffron)] underline underline-offset-4 hover:opacity-80 focus:outline-none"
                  >
                    selecione manualmente
                  </button>
                  .
                </p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-muted)]">
                  formato: pdf · tamanho máx: 10 mb
                </p>
              </div>
            </motion.div>
          )}

          {(state === 'reading' || state === 'parsing') && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-4"
            >
              <ScanLoader />
              <div>
                <p className="font-sans text-sm font-medium text-[var(--ink-primary)]">
                  {state === 'reading' ? 'Lendo PDF...' : 'Identificando seções...'}
                </p>
                {fileName && (
                  <p className="font-mono text-xs text-[var(--ink-tertiary)]">{fileName}</p>
                )}
              </div>
            </motion.div>
          )}

          {state === 'done' && (
            <motion.div
              key="done"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="font-sans text-sm text-[var(--accent-forest)]">
                Perfil parseado. Próximo passo: configurar provider de IA.
              </p>
            </motion.div>
          )}

          {state === 'error' && (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="font-sans text-sm font-medium text-[var(--accent-crimson)]">
                {error}
              </p>
              <button
                type="button"
                onClick={() => {
                  setState('idle');
                  setError(null);
                }}
                className="mt-2 font-sans text-xs text-[var(--ink-secondary)] underline-offset-4 hover:underline"
              >
                Tentar de novo
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

/**
 * Ícone editorial: documento com canto dobrado e linhas internas.
 * Não é cloud-icon SaaS — é página virada de um caderno.
 */
function PaperIcon() {
  return (
    <div
      aria-hidden
      className="relative h-16 w-12 shrink-0 rounded-[2px] border border-[var(--border-default)] bg-[var(--bg-paper)]"
      style={{ boxShadow: '0 4px 8px -6px rgba(27, 31, 35, 0.18)' }}
    >
      {/* canto dobrado superior direito */}
      <div
        className="absolute right-0 top-0 h-3 w-3 border-l border-b border-[var(--border-default)] bg-[var(--bg-paper-elevated)]"
        style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
      />
      {/* linhas internas */}
      <div className="absolute inset-x-2 top-3 space-y-1.5">
        <div className="h-[2px] w-full rounded-full bg-[var(--ink-muted)] opacity-30" />
        <div className="h-[2px] w-3/4 rounded-full bg-[var(--ink-muted)] opacity-30" />
        <div className="h-[2px] w-5/6 rounded-full bg-[var(--ink-muted)] opacity-30" />
        <div className="h-[2px] w-2/3 rounded-full bg-[var(--ink-muted)] opacity-30" />
        <div className="h-[2px] w-4/5 rounded-full bg-[var(--ink-muted)] opacity-30" />
      </div>
      {/* marca-texto saffron na primeira "linha" */}
      <div
        className="absolute left-2 top-3 h-[3px] w-2 rounded-sm"
        style={{ backgroundColor: 'var(--accent-saffron)', opacity: 0.6 }}
      />
    </div>
  );
}

/**
 * Signature: animação de "scan" sobre uma página em miniatura.
 * Linha que percorre verticalmente, simulando leitura.
 */
function ScanLoader() {
  return (
    <div className="relative h-12 w-9 overflow-hidden rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)]">
      {/* "linhas" da página */}
      <div className="flex h-full flex-col justify-around px-1.5 py-1.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-[1.5px] rounded-full bg-[var(--ink-muted)]"
            style={{ width: `${70 + ((i * 13) % 25)}%` }}
          />
        ))}
      </div>
      {/* scanner pass */}
      <motion.div
        className="absolute left-0 right-0 h-[2px] bg-[var(--accent-saffron)]"
        initial={{ top: 0 }}
        animate={{ top: '100%' }}
        transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  );
}

/**
 * Hook auxiliar (export para outros componentes referenciarem se quiserem).
 */
export function useScanState() {
  const [, forceRender] = useState({});
  useEffect(() => {
    const id = setInterval(() => forceRender({}), 100);
    return () => clearInterval(id);
  }, []);
}
